import { themeConfig } from "@gap/shared/theme"
import { DirectiveEditorProps, readOnly$, useCellValue, useMdastNodeUpdater } from "@mdxeditor/editor"
import Box from "@mui/material/Box"
import { observer } from "mobx-react-lite"
import useStore from "reusable/mobx-store-utils"
import GapMindsetComponentStore from "./GapMindsetComponentStore"
import useInitialMount from "reusable/hooks/useInitialMount"
import SkeletonLoader from "reusable/mui/skeleton/SkeletonLoader"
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined"
import Typography from "@mui/material/Typography"

const HANDLE_SIZE = 8

const handlePositions = [
   { top: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2, cursor: 'nwse-resize' },
   { top: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2, cursor: 'nesw-resize' },
   { bottom: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2, cursor: 'nesw-resize' },
   { bottom: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2, cursor: 'nwse-resize' },
]

export default observer(function GapMindsetComponent({ mdastNode }: DirectiveEditorProps) {
   const isReadOnly = useCellValue(readOnly$)
   const updateMdastNode = useMdastNodeUpdater()

   const gapMindsetComponentStore = useStore(sp => new GapMindsetComponentStore(sp), {
      updateMdastNode,
      attributes: mdastNode.attributes as Record<string, string> | undefined
   })

   useInitialMount(() => {
      gapMindsetComponentStore.getDownloadUrl()
   })

   if (gapMindsetComponentStore.loading) {
      return <Box contentEditable={false} sx={{ my: 1 }}>
         <SkeletonLoader
            loading
            transition={false}
            variant="rectangular"
            width={gapMindsetComponentStore.width ?? 300}
            height={gapMindsetComponentStore.height ?? 200}
            sx={{ maxWidth: '100%', borderRadius: 1 }}
         />
      </Box>
   }

   if (gapMindsetComponentStore.error || !gapMindsetComponentStore.downloadUrl) {
      return <Box
         contentEditable={false}
         sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: '#fef2f2',
            border: `1px solid ${themeConfig.primary['200']}`,
            borderRadius: 1,
            px: 1.5,
            py: 0.75,
            my: 0.5,
         }}
      >
         <BrokenImageOutlinedIcon sx={{ fontSize: 18, color: themeConfig.primary['400'] }} />
         <Typography variant="body2" sx={{ color: themeConfig.primary['500'] }}>
            Mindset image unavailable
         </Typography>
      </Box>
   }

   const imgStyle: React.CSSProperties = {
      maxWidth: '100%',
      display: 'block',
      ...(gapMindsetComponentStore.width ? { width: gapMindsetComponentStore.width, height: 'auto' } : { maxHeight: 400 }),
   }

   return <Box contentEditable={false} sx={{ my: 1 }} ref={gapMindsetComponentStore.containerRef}>
      <Box
         sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}
         onMouseEnter={gapMindsetComponentStore.handleMouseEnter}
         onMouseLeave={gapMindsetComponentStore.handleMouseLeave}
      >
         <img
            ref={gapMindsetComponentStore.imgRef}
            src={gapMindsetComponentStore.downloadUrl}
            alt="Mindset image"
            onError={gapMindsetComponentStore.handleImageError}
            style={imgStyle}
         />
         {!isReadOnly && gapMindsetComponentStore.hovered && handlePositions.map((pos, i) => (
            <Box
               key={i}
               data-handle-index={i}
               onPointerDown={gapMindsetComponentStore.handlePointerDown}
               sx={{
                  position: 'absolute',
                  width: HANDLE_SIZE,
                  height: HANDLE_SIZE,
                  backgroundColor: themeConfig.primary['500'],
                  border: '1px solid white',
                  borderRadius: '2px',
                  zIndex: 10,
                  ...pos,
               }}
            />
         ))}
      </Box>
   </Box>
})
